
import React, { useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, BarChart3, CalendarPlus } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameDay, parseISO } from "date-fns";
import { episodes, guests } from "@/data/mockData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const Calendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDay = getDay(monthStart);
  
  // Calculate statistics
  const currentDate = new Date();
  const thisMonth = currentDate.getMonth();
  const thisYear = currentDate.getFullYear();
  
  const episodesThisMonth = episodes.filter(ep => {
    if (!ep.recordingDate) return false;
    const recordDate = parseISO(ep.recordingDate);
    return recordDate.getMonth() === thisMonth && recordDate.getFullYear() === thisYear;
  }).length;
  
  const episodesThisYear = episodes.filter(ep => {
    if (!ep.recordingDate) return false;
    const recordDate = parseISO(ep.recordingDate);
    return recordDate.getFullYear() === thisYear;
  }).length;
  
  const completedEpisodes = episodes.filter(ep => 
    ep.status === 'completed' || ep.status === 'published'
  ).length;
  
  // Upcoming episodes for the list view
  const upcomingEpisodes = episodes
    .filter(ep => ep.status === 'scheduled' || ep.status === 'pending')
    .sort((a, b) => {
      if (!a.recordingDate) return 1;
      if (!b.recordingDate) return -1;
      return new Date(a.recordingDate).getTime() - new Date(b.recordingDate).getTime();
    });

  const getEpisodesForDay = (day: Date) => {
    return episodes.filter(episode => {
      if (!episode.recordingDate) return false;
      try {
        const episodeDate = parseISO(episode.recordingDate);
        return isSameDay(episodeDate, day);
      } catch (error) {
        return false;
      }
    });
  };

  const previousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const getGuestName = (guestId: string) => {
    const guest = guests.find(g => g.id === guestId);
    return guest ? guest.name : "Unknown Guest";
  };

  return (
    <div className="container mx-auto py-8 px-4 md:px-6 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">Recording Calendar</h1>
          <p className="text-muted-foreground mt-1">Manage your podcast recording schedule</p>
        </div>
        <Button className="bg-primary hover:bg-primary/90">
          <CalendarIcon className="mr-2 h-4 w-4" />
          Add Recording
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{episodesThisMonth}</div>
            <p className="text-muted-foreground text-sm">recordings scheduled</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium">This Year</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{episodesThisYear}</div>
            <p className="text-muted-foreground text-sm">recordings scheduled</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{completedEpisodes}</div>
            <p className="text-muted-foreground text-sm">total recordings</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle>
                  {format(currentMonth, "MMMM yyyy")}
                </CardTitle>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={previousMonth}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    onClick={nextMonth}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                  <div 
                    key={day} 
                    className="text-center text-sm font-medium text-muted-foreground p-2"
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: startDay }).map((_, index) => (
                  <div key={`empty-${index}`} className="p-2"></div>
                ))}
                {monthDays.map((day) => {
                  const dayEpisodes = getEpisodesForDay(day);
                  const hasEpisodes = dayEpisodes.length > 0;
                  
                  return (
                    <div 
                      key={day.toString()} 
                      className={`p-1 min-h-[80px] border rounded-md ${
                        isSameDay(day, new Date()) 
                          ? 'bg-primary/5 border-primary/20' 
                          : 'hover:bg-muted/40'
                      }`}
                    >
                      <div className="text-right p-1 font-medium">
                        {format(day, "d")}
                      </div>
                      {hasEpisodes && (
                        <div className="mt-1 space-y-1">
                          {dayEpisodes.slice(0, 2).map((episode) => (
                            <div 
                              key={episode.id}
                              className="text-xs p-1 bg-primary/10 rounded truncate"
                              title={episode.title}
                            >
                              {episode.title.substring(0, 16)}
                              {episode.title.length > 16 ? "..." : ""}
                            </div>
                          ))}
                          {dayEpisodes.length > 2 && (
                            <div className="text-xs text-muted-foreground text-center">
                              +{dayEpisodes.length - 2} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Upcoming Recordings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {upcomingEpisodes.length > 0 ? (
                upcomingEpisodes.map((episode) => (
                  <div key={episode.id} className="space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-sm">{episode.title}</h4>
                        <p className="text-xs text-muted-foreground">
                          Guest: {getGuestName(episode.guestId)}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {episode.status}
                      </Badge>
                    </div>
                    {episode.recordingDate && (
                      <div className="text-xs flex items-center text-muted-foreground">
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        {format(parseISO(episode.recordingDate), "MMM d, yyyy - h:mm a")}
                      </div>
                    )}
                    <Separator className="my-2" />
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No upcoming recordings scheduled</p>
                  <Button variant="outline" className="mt-4">
                    <CalendarPlus className="h-4 w-4 mr-2" />
                    Schedule a Recording
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
